// Pure helpers for the ADA/WCAG-gated screen-reader announcer on the chat
// surfaces (fullscreen chat + admin preview). Extracted from the component
// effects so the toggle-transition behavior (ON→OFF→ON) is unit-testable
// without a DOM (see webapp test-harness convention).
//
// Contract locked in by tests (src/lib/__tests__/a11y-toggle.test.ts):
// - Toggle OFF mid-response cancels the pending typing/reply announcement
//   (the machine is dropped, the live-region text is cleared).
// - Re-enabling never re-announces old history — the machine re-baselines on
//   the existing transcript; only replies appended AFTER re-enable announce.
// - While OFF no announcer node exists in the DOM (`shouldRenderAnnouncer`),
//   except transiently while an essential error status (WCAG 4.1.3 — send
//   failure, quota limit) is being announced, which stays on regardless of
//   the toggle by user decision.

import {
  type ChatAnnouncerState,
  type ChatMessageLike,
  computeChatAnnouncement,
} from "./a11y";

/**
 * Whether the sr-only live-region node should be in the DOM at all.
 * ON → always rendered (persistent region announces text changes reliably).
 * OFF → rendered only while an essential status announcement is active, so
 * that "toggle OFF" genuinely means zero announcer nodes in the idle DOM.
 */
export function shouldRenderAnnouncer(enabled: boolean, announcement: string): boolean {
  return enabled || announcement !== "";
}

/**
 * Essential status messages (WCAG 4.1.3) that must stay programmatically
 * determinable regardless of the ADA/WCAG toggle: send failures, auth/session
 * errors, quota limits, live-chat errors. Detected via the error id suffixes
 * used at every error append site in the fullscreen chat ("-err", "-limit",
 * "live-err-", "v-err-", "f-err-").
 */
export const isEssentialStatusMsg = (m: { id: string }): boolean =>
  /-err$|-limit$/.test(m.id) || /^(live-err|v-err|f-err)-/.test(m.id);

// ── Fullscreen chat ─────────────────────────────────────────────────────────

export interface FullscreenAnnouncerState {
  /** Announce-once state machine; null while the toggle is OFF. */
  machine: ChatAnnouncerState | null;
  /** Id of the essential error already announced while OFF. */
  essentialSeenId: string;
  /** Conversation the essential tracker is baselined on (undefined = none yet). */
  essentialConvo: string | null | undefined;
  /** Current live-region text ("" = nothing to announce). */
  announcement: string;
}

export const initialFullscreenAnnouncerState: FullscreenAnnouncerState = {
  machine: null,
  essentialSeenId: "",
  essentialConvo: undefined,
  announcement: "",
};

export function advanceFullscreenAnnouncer(
  prev: FullscreenAnnouncerState,
  args: {
    enabled: boolean;
    convoId: string | null;
    messages: ReadonlyArray<ChatMessageLike>;
    isLoading: boolean;
  },
): FullscreenAnnouncerState {
  const { enabled, convoId, messages, isLoading } = args;
  if (!enabled) {
    // Toggle OFF: cancel any pending narration (machine dropped) — only
    // essential errors are still announced once.
    const last = messages.length > 0 ? messages[messages.length - 1] : null;
    // Conversation switch / hydration: baseline on the restored transcript so
    // an old error from history is never re-announced, and a fresh error in
    // the newly opened conversation is never suppressed.
    if (prev.essentialConvo !== convoId) {
      return {
        machine: null,
        essentialConvo: convoId,
        essentialSeenId: last?.id ?? "",
        announcement: "",
      };
    }
    if (last && isEssentialStatusMsg(last) && prev.essentialSeenId !== last.id) {
      return { ...prev, machine: null, essentialSeenId: last.id, announcement: last.content.slice(0, 400) };
    }
    if (!last || !isEssentialStatusMsg(last)) {
      return { ...prev, machine: null, announcement: "" };
    }
    return { ...prev, machine: null };
  }
  // Toggle ON: pure announce-once state machine (lib/a11y.ts). A null machine
  // (fresh mount OR just re-enabled) re-baselines on the current transcript,
  // so history present at enable time is never read out.
  const next = computeChatAnnouncement(prev.machine, convoId, messages, isLoading);
  return { ...prev, machine: next, announcement: next.announcement };
}

// ── Admin chat preview ──────────────────────────────────────────────────────

export interface PreviewMessageLike {
  role: string;
  content: string;
  isError?: boolean;
}

export interface PreviewAnnouncerState {
  /** Announce-once state machine; null while the toggle is OFF. */
  machine: ChatAnnouncerState | null;
  /** Index of the essential error already announced while OFF (-1 = none). */
  essentialSeenIdx: number;
  /** Current live-region text ("" = nothing to announce). */
  announcement: string;
}

export const initialPreviewAnnouncerState: PreviewAnnouncerState = {
  machine: null,
  essentialSeenIdx: -1,
  announcement: "",
};

export function advancePreviewAnnouncer(
  prev: PreviewAnnouncerState,
  args: {
    enabled: boolean;
    messages: ReadonlyArray<PreviewMessageLike>;
    isLoading: boolean;
  },
): PreviewAnnouncerState {
  const { enabled, messages, isLoading } = args;
  if (!enabled) {
    // Toggle OFF: no automatic reply/typing narration (pending announcement
    // cancelled), but essential error statuses still announce once.
    const lastIdx = messages.length - 1;
    const last = lastIdx >= 0 ? messages[lastIdx] : null;
    // Transcript reset (preview restart / role switch): re-baseline so a
    // fresh error at a lower index than the previously seen one still
    // announces instead of being suppressed by a stale index.
    const seen = prev.essentialSeenIdx > lastIdx ? -1 : prev.essentialSeenIdx;
    if (last?.isError && seen !== lastIdx) {
      return { machine: null, essentialSeenIdx: lastIdx, announcement: last.content.slice(0, 400) };
    }
    if (!last?.isError) {
      return { machine: null, essentialSeenIdx: seen, announcement: "" };
    }
    return { machine: null, essentialSeenIdx: seen, announcement: prev.announcement };
  }
  // Preview messages have no ids, so index ids are synthesized: transcript
  // resets (role switch, preview restart) drop the previously-tracked newest
  // id and re-baseline instead of re-announcing restored/welcome messages.
  const next = computeChatAnnouncement(
    prev.machine,
    "preview",
    messages.map((m, i) => ({ id: String(i), role: m.role, content: m.content })),
    isLoading,
  );
  return { ...prev, machine: next, announcement: next.announcement };
}
